import { ApiTypes } from './api';
import getLangFromLocale from '@/utils/singUtil/getLangFromLocale';
import { getCountryRegion } from '@/utils/countryRegion';

const NEXT_PUBLIC_API_HOST = process.env.NEXT_PUBLIC_API_HOST;
const NEXT_PUBLIC_API_HOST_CN = process.env.NEXT_PUBLIC_API_HOST_CN;
const NEXT_PUBLIC_API_HOST_EU = process.env.NEXT_PUBLIC_API_HOST_EU;
const NEXT_PUBLIC_LOGIN_HOST = process.env.NEXT_PUBLIC_LOGIN_HOST;
const NEXT_PUBLIC_LOGIN_HOST_CN = process.env.NEXT_PUBLIC_LOGIN_HOST_CN;
const NEXT_PUBLIC_LOGIN_HOST_EU = process.env.NEXT_PUBLIC_LOGIN_HOST_EU;
const NEXT_PUBLIC_WEB_HOST = process.env.NEXT_PUBLIC_WEB_HOST;

interface BaseHttpOtionType {
    url: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    data?: { [key: string]: any };
    query?: { [key: string]: any };
    isForm?: boolean;
    headers?: Record<string, string>;
}
/**
 * 统一请求接口
 * @param {BaseHttpOtionType} option - 请求对应配置
 * @param {BaseHttpOtionType['url']} option.url - 请求地址
 * @param {BaseHttpOtionType['method']} option.method - 请求类型
 * @param {BaseHttpOtionType['query']} option.query - 请求数据
 * @param {BaseHttpOtionType['data']} option.data - 请求body数据
 * @param {BaseHttpOtionType['isForm']} option.isForm - 是否是表带提交
 * @param {BaseHttpOtionType['headers']} option.headers - 头部
 * @returns
 */
const baseHttp = async (option: BaseHttpOtionType) => {
    const { url, method = 'POST', data = {}, query = {}, isForm = false, headers } = option;
    const fetchOption: RequestInit = {
        method,
        mode: 'cors',
        credentials: 'same-origin',
        headers: {
            'Content-Type': isForm ? 'application/x-www-form-urlencoded' : 'application/json',
            ...headers,
        },
        redirect: 'follow',
        referrerPolicy: 'no-referrer',
    };
    let search = '';

    if (method === 'POST') {
        fetchOption.body = JSON.stringify(data);
    } else if (method === 'GET') {
        const param = Object.keys(query)
            .map((key) => `${key}=${query[key]}`)
            .join('&');
        search = param ? `?${param}` : '';
    }

    const response = await fetch(`${url}${search}`, fetchOption);

    return response;
};

export type RequestOtionType = Omit<BaseHttpOtionType, 'url' | 'data' | 'query'> & {
    locale?: string;
    module?: 'default' | 'login';
} & ApiTypes[number];

/**
 * 统一请求接口
 * 使用方式案例 request/README.md
 * @param {RequestOtionType} option - 请求对应配置
 * @param {RequestOtionType['url']} option.url - 请求地址 在 request/api.ts 配置
 * @param {RequestOtionType['method']} option.method - 请求类型
 * @param {RequestOtionType['query']} option.query - 请求数据
 * @param {RequestOtionType['data']} option.data - 请求数据
 * @param {RequestOtionType['isForm']} option.isForm - 是否是表带提交
 * @param {RequestOtionType['locale']} option.locale - 站点 服务端调用必须传
 * @param {RequestOtionType['module']} option.module - 登录/默认,调用地址不一样
 * @returns
 */
export default async function request(option: RequestOtionType) {
    const isSever = typeof window === 'undefined';
    const isLocal = process.env.NODE_ENV === 'development';
    const finallyResp: { success: boolean; error: string; data: any } = {
        success: true,
        error: '',
        data: undefined,
    };
    const { locale, module = 'default', url, ...otherOption } = option;

    //  站点处理 ========
    let finallyLocal = '';
    if (isSever) {
        if (!locale) throw new Error('服务端调用必须传 locale');
        finallyLocal = locale;
    } else {
        finallyLocal = window.location.pathname.substring(1).split('/')[0] || 'us';
    }

    // 前缀处理 ========
    // 登录接口另外前缀
    let doman = module == 'login' ? NEXT_PUBLIC_LOGIN_HOST : NEXT_PUBLIC_API_HOST;
    // 调用的是 pages/api
    if (url.indexOf('/api/') === 0) {
        if (isSever) {
            // 本地开发调用本地服务
            if (isLocal) {
                const port = process.env.npm_lifecycle_script?.split('-p ').pop();
                doman = `http://localhost:${port}`;
            } else {
                doman = NEXT_PUBLIC_WEB_HOST;
            }
        } else {
            doman = '';
        }
    }
    // 区分CN/EU站点
    else if (getCountryRegion(finallyLocal) == 'cn') {
        doman = module == 'login' ? NEXT_PUBLIC_LOGIN_HOST_CN : NEXT_PUBLIC_API_HOST_CN;
    } else if (getCountryRegion(finallyLocal) == 'eu') {
        doman = module === 'login' ? NEXT_PUBLIC_LOGIN_HOST_EU : NEXT_PUBLIC_API_HOST_EU;
    }

    const { acceptLanguage, appId } = getLangFromLocale(finallyLocal) as any;

    const opt = {
        ...otherOption,
        url: `${doman}${url}`,
        headers: {
            'Accept-Language': acceptLanguage,
            'x-appid': appId,
        },
    };

    const resp = await baseHttp(opt);

    if (resp.status !== 200) {
        finallyResp.success = false;
        finallyResp.error = resp.statusText;
        return finallyResp;
    }

    const contentType = resp.headers.get('Content-Type');
    if (contentType && contentType.indexOf('json') != -1) {
        finallyResp.data = await resp.json();
    } else {
        finallyResp.data = await resp.text();
    }

    return finallyResp;
}
